/* ===================================================================
   P5-16 — the stepped buy flow at 749px and below.

   The review's finding: "The configurator stacks several sequential choices
   (style, photo, package, varietal, quantity) into one long section, which tends
   to become a long, thumb-heavy scroll on a phone"
   (docs/soul-dog-wines-website-review.html:328). This turns that column into one
   decision per screen.

   This file holds almost nothing and touches nothing. Every step is marked
   server-side with `data-sdw-step`, every label is server-rendered in
   `snippets/buy-flow-steps.liquid`, and `assets/generator.css` decides what is
   visible. All this script does is write three attributes and one custom property
   on <html>:

     data-sdw-flow   on | absent   is the flow running at all
     data-sdw-step   the step showing
     data-sdw-at     first | mid | last | only, for the Back/Next buttons
     --sdw-flow-progress            the progress bar's width

   **It writes nothing inside the buy box, on purpose.** Selecting a package on
   the home page morphs the whole `featured-product-information` element from
   fresh server HTML — `assets/variant-picker.js` sets `morphElementSelector` to
   that element whenever the picker is inside a featured-product section, and on
   that path it dispatches no `variant:update` either — so a class or an attribute
   written anywhere inside the buy box is reverted mid-flow with no event to hang a
   repair off. <html> is outside every morph target on the page, and the step
   markers survive a morph because the server renders them too.

   Progressive enhancement is the other half: with this file absent, blocked or
   broken, no attribute is ever set, no rule in generator.css matches, the nav bar
   keeps the `hidden` it is rendered with, and the buy box is the single long
   column it was before this row.
   =================================================================== */

(() => {
  /* The tag that loads this file is inside the configurator's own snippet, so a
     second configurator block on one page would load it twice — and every listener
     below is on `document`, so a Next tap would advance two steps. */
  if (window.__sdwBuyFlowSteps) return;
  window.__sdwBuyFlowSteps = true;

  const FLOW_SCOPE = '.product-details';
  /* The section wrapper that holds both columns. `.product-information__media` is a
     grid sibling of `.product-details` and stacks *above* it at this width, so on
     the two screens that show the stage the top of the screen is this element's
     top, not the column's. */
  const SECTION_SCOPE = '.product-information';
  /* The two screens the stage renders on: 1 is *your wines* and 9 is *review and
     add to cart*. Named rather than derived, because these numbers are identity
     and not sequence — `liveSteps()` does not sort, so "the last two" would be a
     different pair the moment a block order changes. */
  const MEDIA_STEPS = new Set([1, 9]);
  const ROOT = document.documentElement;
  const mobile = window.matchMedia('(max-width: 749px)');

  /* No configurator, no flow. This used to say the configurator was home-only and
     that the product page renders steps from the same shared blocks and must not
     step; the product page carries the configurator now, so the test below finds
     it there and that page steps too, which is the point of the two rails being
     the same rail. A template with only the shared blocks still has no
     `ai-pet-generator-block` and still does not step. */
  const configurator = () => document.querySelector('[data-section-id="ai-pet-generator-block"]');

  /* A step counts only if it is on the page and has something on it, and a step
     nobody can answer must not get a screen of its own. Read fresh every time
     rather than cached, so a morph or a package change needs no bookkeeping to be
     picked up.

     **This used to cite `variant-main-picker.liquid`'s `data-hide-when-first` as
     the thing that retired the varietal fieldset for the two 3-bottle packages. No
     file ever read that attribute.** It is deleted now, and the sentence is
     corrected rather than carried: a comment asserting behaviour the theme does not
     have is worse than no comment, because the next reader spends their time
     looking for the mechanism instead of for the bug.

     What actually retires a step is `hidden`, from the server or from a script —
     which is what `.ai-results` uses, and it is why the rail reads three screens
     until the first labels land. */
  const isLive = (el) =>
    !el.hasAttribute('hidden') &&
    !el.closest('[hidden]') &&
    (el.childElementCount > 0 || (el.textContent || '').trim() !== '');

  /* **The order is the rail's, not the numbers'.** This used to end in
     `.sort((a, b) => a - b)`, which made the mobile flow's sequence a *second*
     statement of the order — one held in the step numbers here, the other in the
     block order stored in the JSON templates, and the visible numeral beside each
     heading in a third place again, the DOM-order CSS counter in
     `snippets/variant-main-picker.liquid`. They agreed only because someone kept
     them agreeing.

     Moving the label step above the package step is what broke the tie: the rail
     and the counter both followed the move on their own, and the numeric sort
     alone did not, so a phone would have stepped packages -> wine -> label while
     the same column on a desktop read label -> packages -> wine.

     `querySelectorAll` is already in DOM order and `Set` keeps insertion order, so
     dropping the sort makes the flow follow the rail by construction. The numbers
     keep meaning exactly what they meant — 3 is still the label step in a log or a
     stored preference, which is the guarantee `D-113` and `D-114` left the gaps at
     4, 5 and 6 to protect. They are identity now, and no longer sequence. */
  const liveSteps = () => {
    const scope = document.querySelector(FLOW_SCOPE);
    if (!scope) return [];
    const seen = new Set();
    for (const el of scope.querySelectorAll('[data-sdw-step]')) {
      const n = Number(el.getAttribute('data-sdw-step'));
      if (Number.isInteger(n) && isLive(el)) seen.add(n);
    }
    return [...seen];
  };

  /* **`NaN` when the attribute is absent, and that is a fix rather than a
     tidy-up.** `getAttribute` returns `null` for a missing attribute and
     `Number(null)` is **0** — an integer — so on first load `setStep` took the
     "snap to the nearest live step" branch with a target of 0 and landed on
     whichever step had the smallest number. That was step 1 and it was correct
     while step 1 was the first screen.

     It is not the first screen any more. `D-163` made the rail follow DOM order and
     the four-screen rework put the label step in front of the package step, so a
     visitor arriving at the configurator opened on *your wines* — the third screen
     — with Back available and two screens behind them they had never seen.

     `Number(undefined)` is `NaN`, which `Number.isInteger` rejects, so the absent
     case now falls through to `steps[0]` — the first step in DOM order, which is
     the branch that was written for exactly this and could never be reached.
     Measured in the rig: `data-sdw-step` went from 1 to 3 on load, and the rail's
     own readout from "Step 3 of 5" to "Step 1 of 5". */
  const currentStep = () => {
    const raw = ROOT.getAttribute('data-sdw-step');
    return raw === null ? NaN : Number(raw);
  };

  const prefersReducedMotion = () => !!window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches;

  /* Puts the top of the buy box on screen after a step change, so the visitor is
     not left looking at the middle of a screen that has just been replaced by a
     shorter one. Focus needs no moving: the nav bar is fixed to the bottom of the
     viewport and is never one of the things that gets hidden. */
  /* **The target is the section on the screens that show the stage, and the column
     on the ones that do not.** This scrolled `.product-details` unconditionally,
     which was right while the stage was a fixed overlay on a phone and is wrong
     now: on screens 3 and 4 the stage renders in `.product-information__media`,
     *above* `.product-details` in the same grid, so putting the column's top edge
     at the top of the viewport pushes the bottles off the screen at the moment they
     are meant to be the first thing seen.

     Both elements already carry `scroll-margin-block-start` for the sticky header —
     `.product-information` in `snippets/product-information-content.liquid`, the
     column from this file's own rule in `assets/generator.css` — so neither lands
     underneath it.

     The stage is tested for rather than assumed from the step number: a template
     that renders the shared blocks without a configurator has no stage to scroll
     to, and the column is the right target there. */
  const revealTop = () => {
    const stage = MEDIA_STEPS.has(currentStep())
      ? document.querySelector(`${SECTION_SCOPE} .product-information__media .sdw-stage-root`)
      : null;
    const scope = (stage && stage.closest(SECTION_SCOPE)) || document.querySelector(FLOW_SCOPE);
    if (!scope) return;
    try {
      scope.scrollIntoView({ block: 'start', behavior: prefersReducedMotion() ? 'auto' : 'smooth' });
    } catch (_) {
      scope.scrollIntoView(true);
    }
  };

  /* The step change is a CSS swap, and a live region announces on a DOM mutation,
     so the bar's own progress line can never announce anything however it is
     marked up. This is the region that does: created on <body>, which is outside
     every morph target, and filled from the same spans the bar renders, so the
     wording lives in one place — `snippets/buy-flow-steps.liquid`. */
  let announcer = null;
  const announce = (step, position, total) => {
    /* The step names moved out of this bar and into `blocks/purchase-header.liquid`,
       which renders above every screen and is where the visible title now is. One
       server-rendered source for the wording, read from wherever it lives. */
    const label = document.querySelector(`[data-sdw-title-for="${step}"]`);
    if (!label) return;
    if (!announcer) {
      announcer = document.createElement('p');
      announcer.className = 'visually-hidden';
      announcer.setAttribute('aria-live', 'polite');
      announcer.setAttribute('aria-atomic', 'true');
      document.body.appendChild(announcer);
    }
    announcer.textContent = `Step ${position} of ${total}: ${label.textContent.trim()}`;
  };

  /* Snaps to the nearest live step, so the flow can never land on a screen with
     nothing on it — which is exactly what happens if the visitor is standing on
     the varietal step and then picks a package that has no varietals. */
  const setStep = (wanted, { scroll = false } = {}) => {
    const steps = liveSteps();
    if (!steps.length) return;

    const target = Number.isInteger(wanted) ? wanted : steps[0];
    const next = steps.includes(target)
      ? target
      : steps.reduce((best, n) => (Math.abs(n - target) < Math.abs(best - target) ? n : best), steps[0]);

    const index = steps.indexOf(next);
    const changed = next !== currentStep();

    ROOT.setAttribute('data-sdw-step', String(next));
    ROOT.setAttribute(
      'data-sdw-at',
      steps.length === 1 ? 'only' : index === 0 ? 'first' : index === steps.length - 1 ? 'last' : 'mid'
    );
    ROOT.style.setProperty('--sdw-flow-progress', `${((index + 1) / steps.length) * 100}%`);

    applyGate();
    paintCounter(index + 1, steps.length);
    if (changed) announce(next, index + 1, steps.length);
    if (changed && scroll) revealTop();
  };

  const move = (direction) => {
    const steps = liveSteps();
    const at = steps.indexOf(currentStep());
    const from = at === -1 ? 0 : at;

    /* **Next is the generate press on the first screen, and the wait becomes the
       transition.** Screen 1 asks for the photograph and the name and has no button
       of its own any more; pressing Next validates both, fires the three requests
       and lets the existing full-viewport overlay play over the whole screen. The
       visitor lands on the results with the labels already there, instead of
       watching a wait finish on a screen they then have to leave.

       This file still knows nothing about generation. `assets/generator.js` owns
       the round and publishes two functions; what happens next is its business, and
       the flow is advanced from there by the `sdw:reveal` this script already
       answers.

       **It cannot re-generate.** `pending()` is false the moment any label exists,
       so a visitor who comes back to screen 1 with three labels made presses Next
       and simply advances. Only the redraw button on that screen, or a miss card's
       retry, draws again — which is `D-143`'s rule about what survives a press,
       applied one screen further along.

       A failed validation returns `false` and nothing moves: the photo and name
       messages render in place, on the screen the visitor is standing on, and
       `assets/generator.css` exempts a step carrying a live `[data-ai-error]` from
       being hidden at all. A press that starts a round returns `true` and also does
       not move — the overlay is up, and the flow advances when the round lands.

       Guarded on the step being live and on the function existing, so the bare
       theme, a blocked `generator.js` and the product page all fall through to the
       ordinary advance. */
    if (direction > 0 && steps[from] === 3) {
      const generator = window.SDW_GENERATE;
      try {
        if (generator?.pending?.()) {
          generator.press();
          return;
        }
      } catch (_) {
        /* A generator that throws must not strand the visitor on screen 1 with a
           Next that does nothing. Fall through and advance. */
      }
    }

    /* **Back on the results screen is Try again, and it clears the dog.**

       The bar renders both words and `assets/generator.css` shows one; this is the
       other half. A visitor with three labels and no good one does not want the
       previous screen with their photograph still attached — they want it empty,
       ready for a different photograph, which is also what makes Next generate
       again rather than walk them forward past the same three labels.

       The clearing is `assets/generator.js`'s, not this file's: it owns the fields,
       the stored state and the stage's name, and this script still writes nothing
       inside the buy box. Guarded, so a blocked generator leaves Back as an
       ordinary Back rather than a control that does nothing. */
    if (direction < 0 && steps[from] === 10) {
      try { window.SDW_GENERATE?.clearInputs?.(); } catch (_) {}
    }

    const to = from + direction;
    if (to < 0 || to >= steps.length) return;

    /* **Advancing past the package screen is a package choice.** `D-38` preselects
       the first available package, so a visitor who is happy with it never touches
       a card — and an event that only counts taps would miss most of the people
       who chose. `assets/bottle-stage.js` guards the flag, so tapping a card and
       then pressing Next is still one event. */
    if (direction > 0 && steps[from] === 1) {
      const checked = document.querySelector('fieldset[data-sdw-step="1"] input[type="radio"]:checked');
      window.SDW_TRACK_PACKAGE?.(checked?.value, 'advanced');
    }

    setStep(steps[to], { scroll: true });
  };

  /* --------------------------------------------------------------- */
  /* the bar's own visibility                                          */
  /*                                                                   */
  /* Separate from `data-sdw-flow` on purpose. The flow attribute drives the
     column's layout — which step is showing — and must be settled before first
     paint, or the configurator renders as the long column and then collapses
     under the visitor. The bar is a `position: fixed` element over the whole
     viewport, and on the home page that viewport starts on the hero. Rendered
     from load it covered the hero's call to action outright: at 390 x 844 the bar
     took y 725-844 against the button's y 730-786, and every hit test at the
     button's own coordinates returned the bar.

     So the bar waits for the configurator to be on screen and the layout does
     not. The root box is shortened by 35% of the viewport, so the section has to
     come properly up the screen rather than have its top edge peek in at the very
     bottom. Scrolling back up to the hero puts the bar away again and hands the
     hero's button back, and `data-sdw-step` is untouched throughout, so returning
     lands on the same step it left.

     Without `IntersectionObserver` the bar behaves as it did before this row.
     That is the honest fallback: every browser that supports the `:has()` this
     theme already ships has had the observer for years. */
  let navOnScreen = false;

  /* **The bar's height is measured, not guessed, because two things reserve space
     for it.** The stepped column pads its foot so Add to cart never sits under the
     bar, and the expanded stage pads its own so the bottles are not half-hidden by
     it. The bar's height changes with its contents — a long package name wraps,
     the collapsed stage row is absent when there is no variant — so a constant in
     the stylesheet would be right on one phone and wrong on the next. Written to
     `<html>`, which is where every other piece of this flow's state lives and the
     one node outside every morph target on the page. */
  let navSized = null;
  const measureNav = () => {
    const nav = document.querySelector('[data-sdw-flow-nav]');
    if (!nav || !ROOT.hasAttribute('data-sdw-nav')) {
      ROOT.style.removeProperty('--sdw-nav-height');
      return;
    }
    const height = Math.round(nav.getBoundingClientRect().height);
    if (height > 0) ROOT.style.setProperty('--sdw-nav-height', `${height}px`);

    if (typeof window.ResizeObserver === 'function' && navSized !== nav) {
      navSized = nav;
      new ResizeObserver(() => {
        if (!ROOT.hasAttribute('data-sdw-nav')) return;
        const next = Math.round(nav.getBoundingClientRect().height);
        if (next > 0) ROOT.style.setProperty('--sdw-nav-height', `${next}px`);
      }).observe(nav);
    }
  };

  /* **How far along, written where the visitor can see it.**

     The bar showed the screen's *name* — "Your dog" — and the screen already says
     that at the top of itself, so the line spent itself on nothing new. What a
     visitor cannot read off the screen is their position in the sequence.

     The numbers are the ones `announce()` already builds for the live region, so
     the visible readout and the spoken one cannot disagree. Like the gate below it
     is re-derived rather than remembered: `setStep` writes it, `sync()` calls
     `setStep`, and the `MutationObserver` calls `sync()` after every morph — so a
     morph that replaces the bar is followed by the write that fills it again. */
  const paintCounter = (position, total) => {
    const text = `Step ${position}/${total}`;
    for (const el of document.querySelectorAll('[data-sdw-step-count]')) {
      if (el.textContent !== text) el.textContent = text;
    }
  };

  /* **The two things this script writes inside the buy box, and both are re-derived
     rather than remembered.**

     `D-50`'s rule is that state written inside the buy box is reverted by the
     package morph *with no event to hang a repair off*. That is the whole reason
     every other piece of this flow lives on `<html>`. This is the exception the
     rule allows for: `aria-disabled` on the forward control is not state, it is a
     rendering of `SDW_GENERATE.ready()`, and it is written from `sync()` — which
     the `MutationObserver` below already runs after every morph. A morph that
     reverts it is followed immediately by the resync that puts it back.

     What the observer cannot see is a file being chosen or a name being typed;
     neither mutates a node it watches. `assets/generator.js` raises `sdw:genstate`
     for those, and this listens.

     It is deliberately **not** `disabled`. That would take the button out of the
     tab order, announce nothing, and swallow the press that tells the visitor which
     field is missing — `generator.js` writes those two messages on a failed press
     and `assets/generator.css` is built around them. */
  const applyGate = () => {
    const next = document.querySelector('.sdw-flow-nav [data-sdw-flow-next]');
    if (!next) return;
    /* Only the first screen gates. Everywhere else the forward control is an
       ordinary step change and nothing can make it unavailable. Absent the
       generator — blocked, or a template without one — nothing is gated either. */
    const gated = ROOT.getAttribute('data-sdw-step') === '3' && window.SDW_GENERATE;
    let ok = true;
    if (gated) { try { ok = window.SDW_GENERATE.ready() !== false; } catch (_) { ok = true; } }
    if (ok) next.removeAttribute('aria-disabled');
    else next.setAttribute('aria-disabled', 'true');
  };

  document.addEventListener('sdw:genstate', applyGate);

  const applyNav = () => {
    if (navOnScreen && ROOT.hasAttribute('data-sdw-flow')) ROOT.setAttribute('data-sdw-nav', 'on');
    else ROOT.removeAttribute('data-sdw-nav');
    measureNav();
  };

  let navObserver = null;
  let navObserved = null;
  const watchNav = () => {
    const target = configurator()?.closest('.shopify-section') || configurator();
    if (!target || target === navObserved) return;

    if (typeof window.IntersectionObserver !== 'function') {
      navOnScreen = true;
      applyNav();
      return;
    }

    navObserver?.disconnect();
    navObserver = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) navOnScreen = entry.isIntersecting;
        applyNav();
      },
      { rootMargin: '0px 0px -35% 0px' }
    );
    navObserver.observe(target);
    navObserved = target;
  };

  /* --------------------------------------------------------------- */
  /* the gate: later steps closed until the one before is done        */
  /* --------------------------------------------------------------- */
  /* **`D-269` (2026-10-09), staging experiment.** With the theme setting *Open the
     steps one at a time* on, every step after the first unfinished one is drawn
     as its numeral and title only, and opens when the step before it is done.
     A tap on a closed step says "Complete step N first" in a toast inside the
     tapped step's head, at its right end; each head has its own. It does not
     scroll (`D-299`).

     **Only where the long column is.** Inside the stepped phone flow one step is
     on screen at a time already and Next is the gate, so this stands down whenever
     `data-sdw-flow` is on. And only on the product page: the home page's instance
     is capture-only (`D-181`), it has no later step to close.

     **State on <html> again, by number, for the reason this whole file gives:** the
     buy box is morphed from server HTML (`D-50`), so a class written on a step
     would be gone a frame later. `data-sdw-locked` lists the `data-sdw-step`
     numbers that are closed, and `assets/generator.css` hides by number.

     **What "a step" is here is the numeral, not the number.** The steps a visitor
     counts are the `.sdw-step` elements the CSS counter numbers — `.ai-results`
     opts out of the counter because the labels are the outcome of the label step,
     not a step of their own (`assets/generator.css`), and the gift toggle carries
     review's 9 without being a `.sdw-step`. So every `[data-sdw-step]` element in
     DOM order joins the group of the numbered step it is, holds, or follows: the
     results join the label step, the gift toggle joins review.

     **Done means:**
       - the label step: a label is chosen (`.ai-option.selected`, which is
         `generator.js`'s `hasSelectedImage()`). A round adopts its first label
         (`adopt` in `draw()`), so this is true the moment designs land, and true
         on load for a visitor whose labels come back from storage.
       - a step of radios (packages, wine): every fieldset in it has one checked.
         `D-38` preselects a package, so the package step is done as soon as it
         opens, and review opens with it. Requiring a tap on the card that is
         already chosen was the alternative and was not taken: it is a press that
         changes nothing, asked of every visitor, between them and the cart.
       - anything else (review): done. It is last, nothing waits on it. */
  const GATE_PAGE = 'product';
  const gateWanted = () =>
    ROOT.getAttribute('data-sdw-gate-steps') === 'on' &&
    ROOT.getAttribute('data-sdw-page') === GATE_PAGE &&
    !ROOT.hasAttribute('data-sdw-flow') &&
    !!configurator();

  /* A numbered step that is on the page: not `hidden`, not inside a hidden
     ancestor, and rendered (the wine fieldset of a trio is `display: none`, and an
     undisplayed element does not increment the counter, so it is not a numeral
     either). Its position in this list is its numeral. */
  const numberedSteps = (scope) =>
    [...scope.querySelectorAll('.sdw-step')].filter(
      (el) =>
        !el.classList.contains('ai-results') &&
        !el.closest('[hidden]') &&
        el.getClientRects().length > 0
    );

  const gateGroups = () => {
    const scope = document.querySelector(FLOW_SCOPE);
    if (!scope) return [];
    const numbered = numberedSteps(scope);
    const groups = [];
    let current = null;
    for (const el of scope.querySelectorAll('[data-sdw-step]')) {
      const n = Number(el.getAttribute('data-sdw-step'));
      if (!Number.isInteger(n)) continue;
      const head = numbered.find((step) => step === el || el.contains(step));
      if (head && (!current || current.head !== head)) {
        current = { head, numeral: numbered.indexOf(head) + 1, numbers: new Set(), els: [] };
        groups.push(current);
      }
      /* Before the first numbered step there is no group to join. Nothing renders
         there today; if something did, it stays open rather than being guessed at. */
      if (!current) continue;
      current.numbers.add(n);
      current.els.push(el);
    }
    return groups;
  };

  const groupDone = (group) => {
    const has = (selector) => group.els.some((el) => el.matches(selector) || el.querySelector(selector));
    if (has('.ai-label-step')) return has('.ai-option.selected');
    const fieldsets = group.els.flatMap((el) =>
      el.matches('fieldset') ? [el] : [...el.querySelectorAll('fieldset')]
    ).filter((fs) => fs.querySelector('input[type="radio"]') && fs.getClientRects().length > 0);
    return fieldsets.every((fs) => !!fs.querySelector('input[type="radio"]:checked'));
  };

  /* The first unfinished step, kept for the tap handler: the toast names it. */
  let gateBlocker = null;
  let unfoldTimer = 0;
  /* Set a beat after start, once `generator.js` has had its chance to restore a
     stored label (both files are deferred and neither owns the other's order). */
  let gateSettled = false;
  /* **The numerals, written out while the gate runs.** WebKit paints "0" in the
     package step's circle: that fieldset is a size container (the card grid's
     container queries), `container-type` implies style containment, and WebKit
     scopes the fieldset's *own* `counter-increment` inside it, where Chromium and
     the spec do not (`snippets/variant-main-picker.liquid` has the containment
     story). Desktop Safari on live shows the same "0". It was easy to miss in a
     step full of cards; on a closed step the numeral and the title are all there
     is, and "Complete step 1 first" under a "0" makes no sense.

     The gate already knows each step's position, so it states it: one custom
     property per step number on <html>, read by `assets/generator.css` with the
     counter as the fallback. Same number as the counter wherever the counter is
     right. Removed when the gate stands down, which puts the counter back. */
  let numeralProps = [];
  const writeNumerals = (groups) => {
    const next = [];
    for (const group of groups) {
      const owner = group.head.closest('[data-sdw-step]');
      const n = owner?.getAttribute('data-sdw-step');
      if (!n) continue;
      const prop = `--sdw-numeral-${n}`;
      const value = `"${group.numeral}"`;
      if (ROOT.style.getPropertyValue(prop) !== value) ROOT.style.setProperty(prop, value);
      next.push(prop);
    }
    for (const prop of numeralProps) if (!next.includes(prop)) ROOT.style.removeProperty(prop);
    numeralProps = next;
  };

  const applyStepGate = () => {
    if (!gateWanted()) {
      gateBlocker = null;
      ROOT.removeAttribute('data-sdw-gate');
      ROOT.removeAttribute('data-sdw-locked');
      writeNumerals([]);
      return;
    }
    const groups = gateGroups();
    writeNumerals(groups);
    const blocked = groups.findIndex((group) => !groupDone(group));
    gateBlocker = blocked === -1 ? null : groups[blocked];
    const locked = new Set();
    if (blocked !== -1) for (const group of groups.slice(blocked + 1)) group.numbers.forEach((n) => locked.add(n));
    const before = new Set((ROOT.getAttribute('data-sdw-locked') || '').split(' ').filter(Boolean));
    const value = [...locked].join(' ');
    ROOT.setAttribute('data-sdw-gate', 'on');
    if (value) ROOT.setAttribute('data-sdw-locked', value);
    else ROOT.removeAttribute('data-sdw-locked');

    /* **The steps that just opened unfold, and only those.** An animation starts
       when its rule starts to match, so a rule on every step would replay the label
       step's entrance each time review opened. `data-sdw-unfolding` names the
       numbers that left the locked list on this pass, for long enough to play.
       Not on the first pass: a returning visitor's restored label opens everything
       during load, and that is the page arriving, not a step opening. */
    const opened = [...before].filter((n) => !locked.has(Number(n)));
    if (opened.length && gateSettled) {
      ROOT.setAttribute('data-sdw-unfolding', opened.join(' '));
      clearTimeout(unfoldTimer);
      unfoldTimer = setTimeout(() => ROOT.removeAttribute('data-sdw-unfolding'), 700);
    }
  };
  /* The toast. A status region, so the sentence is read out as well as shown.

     **In the head of the step that was tapped, one per head. `D-299`
     (2026-10-23).** It used to be one toast on <body>, centred above the chat
     button, and the tap scrolled back to the blocking step. The operator asked for
     it beside the tapped step's title instead, overlapping the title where the row
     is too narrow for both, then for it to belong to that head — so it scrolls with
     it — and for each step to have its own, so tapping two closed steps shows two.
     The scroll is gone.

     So each head gets a <span> (a review head is a <p>, which cannot hold a <p>),
     made on its first tap and kept, each with its own timer in `gateToasts`.
     **The morph can take it.** The head is server HTML that is morphed in place
     (`D-50`), and a node the server did not send does not survive that. That is
     harmless — it only ever removes a toast — and `isConnected` is checked so the
     next tap makes a new one rather than writing into a detached node. A WeakMap so
     a head the morph replaced takes its entry with it. */
  const gateToasts = new WeakMap();
  const showGateToast = (text, head) => {
    let entry = gateToasts.get(head);
    if (!entry || !entry.el.isConnected || entry.el.parentElement !== head) {
      const el = document.createElement('span');
      el.className = 'sdw-gate-toast';
      el.setAttribute('role', 'status');
      el.setAttribute('aria-live', 'polite');
      head.appendChild(el);
      entry = { el, timer: 0 };
      gateToasts.set(head, entry);
    }
    const { el } = entry;
    /* Centred on the title's line, not the head's box: the card layout's heads
       carry a top padding for their rule line, and centring on the box put the
       toast 8px high. Measured relative to the head, so it holds while scrolling. */
    const title = head.querySelector('.sdw-step__title') || head;
    const headRect = head.getBoundingClientRect();
    const line = title.getBoundingClientRect();
    el.style.setProperty('--sdw-gate-toast-y', `${line.top - headRect.top + line.height / 2}px`);
    el.textContent = text;
    el.classList.remove('is-shown');
    /* Re-trigger the entrance on a second tap while it is still up: one forced
       style read between the two class writes restarts the keyframes. */
    void el.offsetWidth;
    el.classList.add('is-shown');
    clearTimeout(entry.timer);
    entry.timer = setTimeout(() => el.classList.remove('is-shown'), 3000);
  };

  /* **Capture phase, and the tap is swallowed.** A closed step has nothing on it to
     act on, and the review head sits inside the product form, where delegated
     handlers listen; answering first and stopping there means none of them reads a
     tap on a closed step as a tap on something in it. */
  document.addEventListener(
    'click',
    (event) => {
      if (!ROOT.hasAttribute('data-sdw-locked') || !gateBlocker) return;
      const target = event.target instanceof Element ? event.target : null;
      const owner = target?.closest(`${FLOW_SCOPE} [data-sdw-step]`);
      if (!owner) return;
      const locked = (ROOT.getAttribute('data-sdw-locked') || '').split(' ');
      if (!locked.includes(owner.getAttribute('data-sdw-step'))) return;
      event.preventDefault();
      event.stopPropagation();
      /* The head of the step that was tapped, found through its group: the tap can
         land on a head-less part (the gift toggle joins review), and a group's
         `head` is its `.sdw-step`, whose `.sdw-step__head` is the row with the
         numeral and title — the full width of the column. */
      const tapped = gateGroups().find((group) => group.els.includes(owner));
      const head = tapped?.head.querySelector('.sdw-step__head');
      if (head) showGateToast(`Complete step ${gateBlocker.numeral} first`, head);
    },
    true
  );

  const sync = () => {
    const was = ROOT.hasAttribute('data-sdw-flow');

    /* `liveSteps()` is tested before the flow is switched on, not after. Every rule
       in generator.css keys off both `data-sdw-flow` and `data-sdw-step`, so a flow
       turned on with no step to show would hide nothing and stack all five step
       titles in the bar at once.

       This still turns on for the home page. `data-sdw-flow` also drives the
       mobile-only styling that page keeps — the polaroid drop target chief
       among it (`assets/generator.css`, `.ai-upload__dropzone`) — so switching
       it off there to lose the bar would have cost that too. The bar itself,
       and the in-column button it would otherwise duplicate, are settled by
       `[data-sdw-page='index']` rules in `assets/generator.css` instead; this
       attribute states only "the configurator is running on a phone", same as
       it always has. */
    /* **`single` keeps the flow off on a phone. `D-264` (2026-10-08).** The theme
       setting *Mobile configurator → Layout on phones* writes
       `data-sdw-mobile-layout` on <html>, and with it set to `single` the else
       branch below runs exactly as it does on a desktop: no attribute, no step, the
       bar left `hidden`, and every `html[data-sdw-flow='on']` rule in generator.css
       matching nothing — the one long column this file's header promises when it
       does not run at all. */
    const singleColumn = ROOT.getAttribute('data-sdw-mobile-layout') === 'single';
    if (configurator() && mobile.matches && !singleColumn && liveSteps().length) {
      ROOT.setAttribute('data-sdw-flow', 'on');
      setStep(currentStep());
    } else {
      ROOT.removeAttribute('data-sdw-flow');
      ROOT.removeAttribute('data-sdw-at');
      ROOT.style.removeProperty('--sdw-flow-progress');
    }
    applyNav();
    applyGate();
    applyStepGate();

    /* **The one thing another file needs to be told, rather than to guess.**
       `assets/generator.js` collapses the label step's fields behind a disclosure
       above the breakpoint and must not inside the flow, where the fields *are* a
       screen — so it has to know whether the flow is on. Reading the attribute
       directly would put it in a race with this file: both scripts are deferred and
       neither owns the other's load order, so the first `syncFieldsCollapse()`
       could run either side of the first `sync()`.

       An event costs one line and removes the race entirely. It is dispatched only
       on a change, so the ordinary step press fires nothing, and it carries the
       state rather than requiring the listener to re-read it. The attribute is
       still the single source; this only announces the write. */
    const now = ROOT.hasAttribute('data-sdw-flow');
    if (now !== was) {
      try {
        document.dispatchEvent(new CustomEvent('sdw:flow', { detail: { on: now } }));
      } catch (_) {}
    }
  };

  /* --------------------------------------------------------------- */
  /* wiring                                                            */
  /* --------------------------------------------------------------- */

  /* Delegated, so the buttons keep working after the morph replaces them. */
  document.addEventListener('click', (event) => {
    if (!ROOT.hasAttribute('data-sdw-flow')) return;
    const target = event.target instanceof Element ? event.target : null;
    if (!target) return;
    if (target.closest('[data-sdw-flow-next]')) move(1);
    else if (target.closest('[data-sdw-flow-back]')) move(-1);
  });

  /* P5-04's add-to-cart guard names the one thing still missing and then scrolls
     to and focuses that control (`assets/generator.js`,
     `showAtcRequirementMessage`). Three of its four controls sit on steps the
     visitor is not on when they press Add to cart, and neither `scrollIntoView`
     nor `focus` does anything to a `display: none` element — so the message would
     name a control on a screen nobody can see. generator.js dispatches
     `sdw:reveal` on that control first and this brings its step up, so the scroll
     and the focus land on something. Field errors have a second, independent
     route: generator.css exempts any step carrying a live `[data-ai-error]` from
     being hidden at all, which covers the Generate button's own validation, where
     nothing scrolls at all. */
  document.addEventListener('sdw:reveal', (event) => {
    if (!ROOT.hasAttribute('data-sdw-flow')) return;
    const target = event.target instanceof Element ? event.target : null;
    const owner = target?.closest('[data-sdw-step]');
    if (!owner) return;
    /* **`scroll` is opt-in and the guard's callers deliberately do not set it.**
       The add-to-cart guard and the recap's goto links both scroll to a *control*
       themselves once its step is up, so scrolling to the top of the flow first
       would be two scrolls fighting over one press. The generation landing has no
       control to scroll to — the whole screen is the destination — so that one
       call asks for it. */
    setStep(Number(owner.getAttribute('data-sdw-step')), { scroll: !!event.detail?.scroll });
  });

  /* A package change retires or restores the varietal step and morphs the section
     around it. Neither fires an event this script can rely on — the home page's
     morph path dispatches no `variant:update` — so the DOM itself is the signal.
     Cheap: one rAF-collapsed `sync()`, which is a querySelectorAll over the buy
     box and three attribute writes on <html>, and <html> is outside what is
     observed, so this cannot feed itself. */
  let queued = false;
  const resync = () => {
    if (queued) return;
    queued = true;
    window.requestAnimationFrame(() => {
      queued = false;
      sync();
      observe();
      watchNav();
    });
  };

  let observed = null;
  const observer = new MutationObserver(resync);
  const observe = () => {
    const scope = configurator()?.closest('.shopify-section') || document.querySelector(FLOW_SCOPE);
    if (!scope || scope === observed) return;
    observer.disconnect();
    observer.observe(scope, { childList: true, subtree: true, attributes: true, attributeFilter: ['hidden', 'aria-checked'] });
    observed = scope;
  };

  document.addEventListener('variant:update', resync);
  /* `D-269`: the gate also moves on a package or wine tap (a radio's `checked` is a
     property, not an attribute the observer sees) and on the generator's own
     state changes. Both are the same rAF-collapsed resync. */
  document.addEventListener('change', resync);
  document.addEventListener('sdw:genstate', resync);
  mobile.addEventListener('change', sync);

  const start = () => {
    sync();
    observe();
    watchNav();
    setTimeout(() => { gateSettled = true; }, 1200);
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start);
  else start();
})();
